// Impersonation banner — rendered at the top of every authenticated page when the
// current JWT carries an `imp` claim. Lets the super-admin return to their own
// session in one click instead of logging out and back in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ImpersonationBanner() {
  const [session, setSession] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/auth/session");
        if (!cancel) setSession(r.data);
      } catch { /* unauth: banner hidden */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!session?.impersonating) return null;

  const exitImpersonation = async () => {
    try {
      // Sprint Feb 8, 2026 — switched the admin-token cache from
      // sessionStorage to localStorage. sessionStorage is per-tab, so if
      // the admin clicked "Impersonate" in Tab A and the impersonated
      // user's silo opened in Tab B (target=_blank) they couldn't get back
      // to their admin session — Tab B had no admin_original_token and
      // showed "Original admin token missing." localStorage is shared
      // across tabs and survives reloads, so the exit always works.
      // Falls back to sessionStorage for backward compat with any in-flight
      // impersonation sessions started before this fix.
      const original = (
        localStorage.getItem("admin_original_token") ||
        sessionStorage.getItem("admin_original_token") ||
        ""
      );
      if (!original) {
        toast.error("Original admin token missing — please log in again.");
        nav("/login");
        return;
      }
      sessionStorage.setItem("mysilo_token", original);
      localStorage.removeItem("admin_original_token");
      sessionStorage.removeItem("admin_original_token");
      // Feb 21, 2026 (late eve) — earlier I cleared `mysilo_user` here
      // thinking that would force a refresh on the destination page. But
      // <Protected> reads BOTH token AND user synchronously and bounces to
      // /login if either is missing → looked like "exit logged me out".
      //
      // Instead: synchronously re-fetch /auth/me with the restored admin
      // token and re-seed the cache with the REAL super-admin profile
      // BEFORE navigating. That way <Protected> sees a valid super-admin
      // user, and DashboardGate / IdentityPill never see the stale
      // impersonated fan.
      try {
        const me = await api.get("/auth/me");
        if (me?.data) {
          sessionStorage.setItem("mysilo_user", JSON.stringify(me.data));
        }
      } catch {
        // If /auth/me fails (unlikely), leave the cache as-is rather than
        // wiping it — the page reload below will retry.
      }
      toast.success("Returned to super admin.");
      // Feb 21, 2026 (eve) — return directly to /admin where the
      // super-admin was when they hit Impersonate. Creator Dashboard is
      // one click away via the Identity Pill.
      setTimeout(() => { window.location.href = "/admin"; }, 350);
    } catch {
      nav("/login");
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#C16E5A] text-[#FDFCF8] px-4 py-2 text-xs flex items-center justify-between gap-4 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] cta-safe"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye size={14} weight="duotone" />
        <span className="uppercase tracking-[0.18em]">Impersonating</span>
        <span className="truncate font-mono">{session.user?.email}</span>
        <span className="opacity-60 hidden sm:inline">· signed in as super admin {session.impersonating.admin_email}</span>
      </div>
      <button
        type="button"
        onClick={exitImpersonation}
        data-testid="exit-impersonation"
        className="shrink-0 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-[#FDFCF8]/40 hover:bg-[#FDFCF8]/10"
      >
        Exit impersonation
      </button>
    </div>
  );
}
